export type DecryptedBlock = {
  id: string;
  data: unknown;
  type?: string;
};

const DB_NAME = "mk-db";
const DB_VERSION = 1;
const BLOCKS_STORE = "blocks";
const META_STORE = "meta";
const LAST_FETCH_KEY = "blocksLastFetchedAt";

let dbPromise: Promise<IDBDatabase> | null = null;

function openDb(): Promise<IDBDatabase> {
  if (typeof window === "undefined") {
    return Promise.reject(new Error("IndexedDB is not available"));
  }

  if (!dbPromise) {
    dbPromise = new Promise((resolve, reject) => {
      const request = window.indexedDB.open(DB_NAME, DB_VERSION);

      request.onupgradeneeded = () => {
        const db = request.result;
        if (!db.objectStoreNames.contains(BLOCKS_STORE)) {
          db.createObjectStore(BLOCKS_STORE, { keyPath: "id" });
        }
        if (!db.objectStoreNames.contains(META_STORE)) {
          db.createObjectStore(META_STORE);
        }
      };

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error ?? new Error("Failed to open IndexedDB"));
    });
  }

  return dbPromise;
}

function requestToPromise<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error("IndexedDB request failed"));
  });
}

function transactionDone(transaction: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    transaction.oncomplete = () => resolve();
    transaction.onerror = () =>
      reject(transaction.error ?? new Error("IndexedDB transaction failed"));
    transaction.onabort = () =>
      reject(transaction.error ?? new Error("IndexedDB transaction aborted"));
  });
}

export async function getStoredBlocks(): Promise<DecryptedBlock[]> {
  if (typeof window === "undefined") return [];

  try {
    const db = await openDb();
    const tx = db.transaction(BLOCKS_STORE, "readonly");
    const blocks = await requestToPromise(tx.objectStore(BLOCKS_STORE).getAll());
    await transactionDone(tx);
    return blocks as DecryptedBlock[];
  } catch (e) {
    console.error("blockStorage: failed to read blocks", e);
    return [];
  }
}

export async function saveBlocks(blocks: DecryptedBlock[]): Promise<void> {
  if (typeof window === "undefined") return;

  const db = await openDb();
  const tx = db.transaction(BLOCKS_STORE, "readwrite");
  const store = tx.objectStore(BLOCKS_STORE);
  store.clear();
  for (const block of blocks) {
    store.put(block);
  }
  await transactionDone(tx);
}

export async function getLastFetchedAt(): Promise<string | null> {
  if (typeof window === "undefined") return null;

  try {
    const db = await openDb();
    const tx = db.transaction(META_STORE, "readonly");
    const value = await requestToPromise(tx.objectStore(META_STORE).get(LAST_FETCH_KEY));
    await transactionDone(tx);
    return typeof value === "string" && value.length > 0 ? value : null;
  } catch (e) {
    console.error("blockStorage: failed to read last fetch time", e);
    return null;
  }
}

export async function setLastFetchedAt(iso: string): Promise<void> {
  if (typeof window === "undefined") return;

  const db = await openDb();
  const tx = db.transaction(META_STORE, "readwrite");
  tx.objectStore(META_STORE).put(iso, LAST_FETCH_KEY);
  await transactionDone(tx);
}
